/**
 * F32 (Wave 2C) — admin resolve-open-game-report dialog.
 *
 * SegmentedControl resolution picker (reviewed | dismissed, validated against
 * `ResolveOpenGameReportInputSchema`). Posts via the parent's `onResolve` which
 * calls `adminOpenGamesApi.resolveReport`.
 *
 * Gated by the parent page on `open_games:moderate`. Composes F02 primitives
 * only (Modal / FormField / SegmentedControl / Button / Banner).
 */
import { useEffect, useMemo, useState } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import { Banner, Button, FormField, Modal, SegmentedControl } from '@bravemove/ui-web';
import { ResolveOpenGameReportInputSchema, type OpenGameReportResolution } from '@bravemove/types';

export interface ResolveOpenGameReportDialogProps {
  readonly open: boolean;
  readonly onResolve: (resolution: OpenGameReportResolution) => Promise<void>;
  readonly onClose: () => void;
  readonly errorMessage?: string | null;
}

export function ResolveOpenGameReportDialog({
  open,
  onResolve,
  onClose,
  errorMessage,
}: ResolveOpenGameReportDialogProps) {
  const { t } = useI18n();
  const [resolution, setResolution] = useState<OpenGameReportResolution>('reviewed');
  const [submitting, setSubmitting] = useState(false);

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (!open) return;
    setResolution('reviewed');
  }, [open]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const options = useMemo(
    () => [
      {
        value: 'reviewed' as const,
        label: t('open-game:admin.reports_queue.mark_reviewed', { defaultValue: 'Mark reviewed' }),
      },
      {
        value: 'dismissed' as const,
        label: t('open-game:admin.reports_queue.mark_dismissed', { defaultValue: 'Dismiss' }),
      },
    ],
    [t],
  );

  const valid = useMemo(
    () => ResolveOpenGameReportInputSchema.safeParse({ resolution }).success,
    [resolution],
  );

  const submit = async (): Promise<void> => {
    if (!valid) return;
    setSubmitting(true);
    try {
      await onResolve(resolution);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('open-game:admin.reports_queue.resolve', { defaultValue: 'Resolve' })}
      size="md"
      footer={
        <>
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={submitting}
            data-testid="resolve-report-cancel"
          >
            {t('open-game:admin.suspend_dialog.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button
            variant="primary"
            onClick={() => void submit()}
            loading={submitting}
            disabled={!valid || submitting}
            data-testid="resolve-report-submit"
          >
            {t('open-game:admin.reports_queue.resolve', { defaultValue: 'Resolve' })}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3" data-testid="resolve-report-dialog">
        {errorMessage ? <Banner tone="danger">{errorMessage}</Banner> : null}
        <FormField
          label={t('open-game:admin.reports_queue.resolve', { defaultValue: 'Resolution' })}
          required
        >
          <SegmentedControl<OpenGameReportResolution>
            value={resolution}
            onChange={(v) => setResolution(v)}
            options={options}
            fullWidth
            aria-label={t('open-game:admin.reports_queue.resolve', { defaultValue: 'Resolution' })}
          />
        </FormField>
      </div>
    </Modal>
  );
}
